import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ChevronLeft, 
  ChevronRight,
  Fuel, 
  Gauge, 
  Calendar,
  Car as CarIcon,
  Palette,
  Settings,
  ArrowLeft,
  Check,
  X,
  Send,
  Loader2,
  Calculator,
  PoundSterling,
  Info
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { FINANCE_DISCLAIMER } from '../config/dealership';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

// Finance calculation helper (same logic as backend)
const calculateMonthlyPayment = (price, deposit, termMonths, interestRate = 0) => {
  const loanAmount = Math.max(price - deposit, 0);
  
  if (loanAmount === 0) return 0;
  
  if (!interestRate || interestRate === 0) {
    return loanAmount / termMonths;
  }
  
  const r = (interestRate / 100) / 12; // Monthly interest rate
  const n = termMonths;
  const monthly = loanAmount * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  
  return Math.round(monthly * 100) / 100;
};

// Helper function to convert Google Drive share URL to direct image URL
const getDirectImageUrl = (url) => {
  if (!url) return null;
  
  const driveMatch = url.match(/drive\.google\.com\/file\/d\/([^/]+)/);
  if (driveMatch) {
    return `https://drive.google.com/uc?export=view&id=${driveMatch[1]}`;
  }
  
  if (url.includes('drive.google.com/uc')) {
    return url;
  }
  
  return url;
};

const placeholderImage = 'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=800&auto=format&fit=crop';

// Image Gallery Component
const ImageGallery = ({ images, carName }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageErrors, setImageErrors] = useState({});
  
  const processedImages = images.map(img => {
    const url = getDirectImageUrl(img);
    return (url && url.startsWith('http')) ? url : placeholderImage;
  });
  
  const handlePrev = () => {
    setCurrentIndex(prev => (prev === 0 ? processedImages.length - 1 : prev - 1));
  };
  
  const handleNext = () => {
    setCurrentIndex(prev => (prev === processedImages.length - 1 ? 0 : prev + 1));
  };
  
  const handleImageError = (index) => {
    setImageErrors(prev => ({ ...prev, [index]: true }));
  };
  
  const getImageSrc = (index) => {
    return imageErrors[index] ? placeholderImage : processedImages[index];
  };
  
  return (
    <div className="space-y-4" data-testid="image-gallery">
      {/* Main Image - 16:9 aspect ratio with object-contain to show full image */}
      <div className="relative aspect-video bg-black overflow-hidden group">
        <AnimatePresence mode="wait">
          <motion.img
            key={currentIndex}
            src={getImageSrc(currentIndex)}
            alt={`${carName} - Image ${currentIndex + 1}`}
            className="w-full h-full object-contain"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onError={() => handleImageError(currentIndex)}
            data-testid="main-gallery-image"
          />
        </AnimatePresence>
        
        {/* Navigation Arrows */}
        {processedImages.length > 1 && (
          <>
            <button
              onClick={handlePrev}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 flex items-center justify-center text-white transition-all opacity-0 group-hover:opacity-100"
              data-testid="gallery-prev-btn"
              aria-label="Previous image"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={handleNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 flex items-center justify-center text-white transition-all opacity-0 group-hover:opacity-100"
              data-testid="gallery-next-btn"
              aria-label="Next image"
            >
              <ChevronRight size={24} />
            </button>
          </>
        )}
        
        {/* Image Counter */}
        <div className="absolute bottom-4 right-4 bg-black/60 px-3 py-1 text-xs text-white font-sans">
          {currentIndex + 1} / {processedImages.length}
        </div>
      </div>
      
      {/* Thumbnails - keep object-cover for consistent grid look */}
      {processedImages.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin" data-testid="thumbnail-gallery">
          {processedImages.map((img, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`flex-shrink-0 w-20 aspect-[4/3] overflow-hidden border-2 transition-all ${
                index === currentIndex 
                  ? 'border-[#CCAD60]' 
                  : 'border-transparent hover:border-white/30'
              }`}
              data-testid={`thumbnail-${index}`}
            >
              <img
                src={getImageSrc(index)}
                alt={`Thumbnail ${index + 1}`}
                className="w-full h-full object-cover"
                onError={() => handleImageError(index)}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Spec Item Component
const SpecItem = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3 py-3 border-b border-white/10">
    <div className="w-10 h-10 flex items-center justify-center bg-[#141414]">
      <Icon size={18} className="text-[#CCAD60]" strokeWidth={1.5} />
    </div>
    <div>
      <p className="text-[10px] uppercase tracking-[0.15em] text-[zinc-500] font-sans">{label}</p>
      <p className="text-sm text-[white] font-sans font-medium">{value || 'N/A'}</p>
    </div>
  </div>
);

// Main Car Detail Page
const CarDetailPage = () => {
  const { id } = useParams();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const fetchCar = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await fetch(`${API}/cars/${id}`);
        
        if (!response.ok) {
          if (response.status === 404) {
            setError('This vehicle is no longer available.');
          } else {
            setError('Failed to load vehicle details.');
          }
          return;
        }
        
        const data = await response.json();
        setCar(data);
      } catch (err) {
        console.error('Failed to fetch car:', err);
        setError('Failed to load vehicle details.');
      } finally {
        setLoading(false);
      }
    };
    
    if (id) {
      fetchCar();
    }
  }, [id]);
  
  const isSold = car?.status === 'sold';
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#000000] pt-24" data-testid="car-detail-loading">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
          <div className="animate-pulse">
            <div className="h-8 w-48 bg-[#141414] mb-8" />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="aspect-[4/3] bg-[#141414]" />
              <div className="space-y-4">
                <div className="h-10 w-3/4 bg-[#141414]" />
                <div className="h-12 w-1/2 bg-[#141414]" />
                <div className="h-24 bg-[#141414]" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-[#000000] pt-24" data-testid="car-detail-error">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
          <Link 
            to="/cars" 
            className="inline-flex items-center gap-2 text-[#CCAD60] hover:text-white transition-colors mb-8"
            data-testid="back-to-cars-error"
          >
            <ArrowLeft size={18} />
            <span className="text-sm font-sans uppercase tracking-wider">Back to all cars</span>
          </Link>
          
          <div className="text-center py-20">
            <CarIcon size={64} className="mx-auto text-[zinc-500] mb-6" strokeWidth={1} />
            <h1 className="font-serif text-2xl text-[white] mb-4">Vehicle Not Found</h1>
            <p className="text-[zinc-500] font-sans mb-8">{error}</p>
            <Link to="/cars">
              <Button className="bg-[#CCAD60] text-black hover:bg-white">
                Browse Available Cars
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }
  
  if (!car) return null;
  
  return (
    <div className="min-h-screen bg-[#000000] pt-24" data-testid="car-detail-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 md:py-12">
        {/* Breadcrumb */}
        <Link 
          to="/cars" 
          className="inline-flex items-center gap-2 text-[#CCAD60] hover:text-white transition-colors mb-8"
          data-testid="back-to-cars-link"
        >
          <ArrowLeft size={18} />
          <span className="text-sm font-sans uppercase tracking-wider">Back to all cars</span>
        </Link>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Left Column - Gallery */}
          <div>
            {/* Badges */}
            <div className="flex gap-3 mb-4">
              {car.featured && (
                <span className="bg-[#CCAD60] text-black px-3 py-1 text-xs font-bold uppercase tracking-wider">
                  Featured
                </span>
              )}
              {isSold && (
                <span className="bg-red-600 text-white px-3 py-1 text-xs font-bold uppercase tracking-wider" data-testid="sold-badge">
                  SOLD
                </span>
              )}
            </div>
            
            <ImageGallery 
              images={car.images?.length ? car.images : [placeholderImage]} 
              carName={`${car.make} ${car.model}`} 
            />
          </div>
          
          {/* Right Column - Details */}
          <div>
            {/* Title */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[white] tracking-tight mb-2" data-testid="car-title">
                {car.make} {car.model}
              </h1>
              {car.trim && (
                <p className="text-[#CCAD60] font-sans text-sm uppercase tracking-[0.2em] mb-6" data-testid="car-trim">
                  {car.trim}
                </p>
              )}
            </motion.div>
            
            {/* Price */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="mb-8"
            >
              <p className="font-serif text-4xl md:text-5xl font-bold text-[white]" data-testid="car-price">
                €{car.price?.toLocaleString()}
              </p>
              {car.finance_enabled && (
                <p className="text-[zinc-500] font-sans text-sm mt-2">
                  Finance available
                </p>
              )}
            </motion.div>
            
            {/* Key Specs Grid */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="grid grid-cols-2 gap-x-6 mb-8"
              data-testid="car-specs"
            >
              <SpecItem icon={Calendar} label="Year" value={car.year} />
              <SpecItem icon={Gauge} label="Mileage" value={`${car.mileage?.toLocaleString()} miles`} />
              <SpecItem icon={Fuel} label="Fuel Type" value={car.fuel_type} />
              <SpecItem icon={Settings} label="Transmission" value={car.transmission} />
              <SpecItem icon={CarIcon} label="Body Type" value={car.body_type} />
              <SpecItem icon={Palette} label="Colour" value={car.colour} />
            </motion.div>
            
            {/* Engine Size if available */}
            {car.engine_size && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.25 }}
                className="mb-8"
              >
                <p className="text-[10px] uppercase tracking-[0.15em] text-[zinc-500] font-sans mb-1">Engine</p>
                <p className="text-sm text-[white] font-sans">{car.engine_size}</p>
              </motion.div>
            )}
            
            {/* Description */}
            {car.description && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="mb-8"
                data-testid="car-description"
              >
                <h3 className="text-[10px] uppercase tracking-[0.2em] text-[#CCAD60] font-sans font-medium mb-3">
                  Description
                </h3>
                <p className="text-[zinc-500] font-sans text-sm leading-relaxed whitespace-pre-line">
                  {car.description}
                </p>
              </motion.div>
            )}
            
            {/* CTA Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="pt-6 border-t border-white/10"
            >
              {isSold ? (
                <div 
                  className="bg-red-600/10 border border-red-600/30 p-6 text-center"
                  data-testid="sold-notice"
                >
                  <X size={32} className="mx-auto text-red-500 mb-3" />
                  <h3 className="font-serif text-xl text-[white] mb-2">SOLD</h3>
                  <p className="text-[zinc-500] font-sans text-sm mb-4">
                    This vehicle has been sold and is no longer accepting enquiries.
                  </p>
                  <Link to="/cars">
                    <Button className="bg-[#CCAD60] text-black hover:bg-white font-bold uppercase tracking-wider text-xs">
                      Browse Other Cars
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-8">
                  {/* Finance Section - only for finance_enabled cars */}
                  {car.finance_enabled && (
                    <FinanceSection 
                      carId={car.id}
                      carName={`${car.year} ${car.make} ${car.model}${car.trim ? ' ' + car.trim : ''}`}
                      price={car.price}
                      defaultDeposit={car.default_deposit}
                      interestRate={car.interest_rate}
                    />
                  )}
                  
                  {/* General Enquiry Form */}
                  <EnquiryForm 
                    carId={car.id} 
                    carName={`${car.year} ${car.make} ${car.model}${car.trim ? ' ' + car.trim : ''}`} 
                  />
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Finance Section Component
const FinanceSection = ({ carId, carName, price, defaultDeposit, interestRate }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [deposit, setDeposit] = useState(defaultDeposit || 0);
  const [termMonths, setTermMonths] = useState(48);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [phoneError, setPhoneError] = useState(null);
  
  // Calculate monthly payment based on current inputs
  const monthlyPayment = useMemo(() => {
    return calculateMonthlyPayment(price, deposit, termMonths, interestRate || 0);
  }, [price, deposit, termMonths, interestRate]);
  
  // Calculate default monthly payment for summary display
  const defaultMonthlyPayment = useMemo(() => {
    return calculateMonthlyPayment(price, defaultDeposit || 0, 48, interestRate || 0);
  }, [price, defaultDeposit, interestRate]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
    if (name === 'phone') {
      setPhoneError(null);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate phone is provided for finance enquiries
    if (!formData.phone.trim()) {
      setPhoneError('Phone number is required for finance enquiries');
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      const response = await fetch(`${API}/cars/${carId}/enquiries/finance`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone.trim(),
          message: formData.message || null,
          deposit: deposit,
          term_months: termMonths
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to submit finance enquiry');
      }
      
      setIsSubmitted(true);
      toast.success('Finance enquiry sent successfully!');
    } catch (err) {
      console.error('Finance enquiry submission error:', err);
      setError(err.message);
      toast.error(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (isSubmitted) {
    return (
      <div 
        className="bg-[#CCAD60]/10 border border-[#CCAD60]/30 p-6 text-center"
        data-testid="finance-enquiry-success"
      >
        <Check size={32} className="mx-auto text-[#CCAD60] mb-3" />
        <h3 className="font-serif text-xl text-[white] mb-2">Finance Enquiry Sent!</h3>
        <p className="text-[zinc-500] font-sans text-sm">
          Thank you for your finance enquiry. We'll be in touch soon with options tailored to you.
        </p>
      </div>
    );
  }
  
  return (
    <div className="border border-white/10 overflow-hidden" data-testid="finance-section">
      {/* Finance Summary - Always Visible */}
      <div className="bg-gradient-to-r from-[#141414] to-[#0A0A0A] p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Calculator size={18} className="text-[#CCAD60]" />
              <span className="text-[10px] uppercase tracking-[0.15em] text-[#CCAD60] font-sans font-medium">
                Finance Available
              </span>
            </div>
            <p className="font-serif text-2xl sm:text-3xl font-bold text-[white]" data-testid="finance-summary-monthly">
              From €{defaultMonthlyPayment.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}<span className="text-lg font-normal text-[zinc-500]">/month</span>
            </p>
            <p className="text-xs text-[zinc-500] font-sans mt-1" data-testid="finance-summary-details">
              Based on €{(defaultDeposit || 0).toLocaleString()} deposit over 48 months at {interestRate || 0}% APR
            </p>
          </div>
          <Button
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full sm:w-auto bg-[#CCAD60] text-black font-bold text-xs sm:text-xs uppercase tracking-wider hover:bg-white transition-colors h-11 sm:h-10 px-4"
            data-testid="finance-toggle-btn"
          >
            <span className="sm:hidden">{isExpanded ? 'Hide' : 'Finance Calculator'}</span>
            <span className="hidden sm:inline">{isExpanded ? 'Hide Options' : 'Adjust & Apply'}</span>
          </Button>
        </div>
      </div>
      
      {/* Collapsible Finance Panel */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
            data-testid="finance-panel"
          >
            <div className="bg-[#0A0A0A] p-5 border-t border-white/10">
              {/* Calculator Section */}
              <div className="mb-6">
                <h4 className="text-[10px] uppercase tracking-[0.2em] text-[#CCAD60] font-sans font-medium mb-4">
                  Customise Your Finance
                </h4>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  {/* Deposit Input */}
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.15em] text-[zinc-500] font-sans mb-2">
                      Deposit (€)
                    </label>
                    <Input
                      type="number"
                      value={deposit}
                      onChange={(e) => setDeposit(Math.max(0, parseFloat(e.target.value) || 0))}
                      min={0}
                      max={price}
                      className="bg-[#141414] border-white/10 text-[white] h-12 focus:border-[#CCAD60]"
                      data-testid="finance-deposit-input"
                    />
                  </div>
                  
                  {/* Term Select */}
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.15em] text-[zinc-500] font-sans mb-2">
                      Term (months)
                    </label>
                    <Select
                      value={termMonths.toString()}
                      onValueChange={(value) => setTermMonths(parseInt(value))}
                    >
                      <SelectTrigger 
                        className="bg-[#141414] border-white/10 text-[white] h-12"
                        data-testid="finance-term-select"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#141414] border-white/10">
                        <SelectItem value="24" className="text-[white]">24 months</SelectItem>
                        <SelectItem value="36" className="text-[white]">36 months</SelectItem>
                        <SelectItem value="48" className="text-[white]">48 months</SelectItem>
                        <SelectItem value="60" className="text-[white]">60 months</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Live Monthly Payment Display */}
                <div className="bg-[#141414] border border-[#CCAD60]/30 p-4 text-center" data-testid="monthly-payment-display">
                  <p className="text-[10px] uppercase tracking-[0.15em] text-[zinc-500] font-sans mb-1">
                    Your Estimated Monthly Payment
                  </p>
                  <p className="font-serif text-3xl font-bold text-[#CCAD60]">
                    €{monthlyPayment.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                  <p className="text-[10px] text-[zinc-500] font-sans mt-1">
                    {interestRate ? `${interestRate}% APR` : '0% APR'} • {termMonths} months • €{deposit.toLocaleString()} deposit
                  </p>
                </div>
                
                {/* Finance Disclaimer */}
                <div className="flex items-start gap-2 mt-3 p-3 bg-[#141414]/50 border border-white/5" data-testid="finance-disclaimer">
                  <Info size={14} className="text-[zinc-500] mt-0.5 shrink-0" />
                  <p className="text-[11px] text-[zinc-500] font-sans leading-relaxed">
                    {FINANCE_DISCLAIMER}
                  </p>
                </div>
              </div>
              
              {/* Finance Enquiry Form */}
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="finance-enquiry-form">
                <h4 className="text-[10px] uppercase tracking-[0.2em] text-[#CCAD60] font-sans font-medium">
                  Apply for Finance
                </h4>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Name */}
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.15em] text-[zinc-500] font-sans mb-2">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Your name"
                      className="bg-[#141414] border-white/10 text-[white] h-11 focus:border-[#CCAD60]"
                      data-testid="finance-name-input"
                    />
                  </div>
                  
                  {/* Email */}
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.15em] text-[zinc-500] font-sans mb-2">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your@email.com"
                      className="bg-[#141414] border-white/10 text-[white] h-11 focus:border-[#CCAD60]"
                      data-testid="finance-email-input"
                    />
                  </div>
                </div>
                
                {/* Phone */}
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.15em] text-[zinc-500] font-sans mb-2">
                    Phone <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="Your phone number"
                    className={`bg-[#141414] border-white/10 text-[white] h-11 focus:border-[#CCAD60] ${phoneError ? 'border-red-500' : ''}`}
                    data-testid="finance-phone-input"
                  />
                  {phoneError && (
                    <p className="text-red-400 text-xs mt-1" data-testid="finance-phone-error">{phoneError}</p>
                  )}
                </div>
                
                {/* Message */}
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.15em] text-[zinc-500] font-sans mb-2">
                    Additional Info <span className="text-[zinc-500]">(optional)</span>
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Any questions or special requirements?"
                    rows={2}
                    className="bg-[#141414] border-white/10 text-[white] focus:border-[#CCAD60] resize-none"
                    data-testid="finance-message-input"
                  />
                </div>
                
                {/* Error Message */}
                {error && (
                  <div className="bg-red-600/10 border border-red-600/30 p-3 text-red-400 text-sm font-sans" data-testid="finance-error">
                    {error}
                  </div>
                )}
                
                {/* Submit Button */}
                <Button 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full h-12 bg-[#CCAD60] text-black font-bold uppercase tracking-widest text-xs hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="finance-submit-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <PoundSterling size={16} className="mr-2" />
                      Submit Finance Application
                    </>
                  )}
                </Button>
              </form>
              
              {/* Collapse Link */}
              <button
                onClick={() => setIsExpanded(false)}
                className="w-full mt-4 py-2 text-[10px] uppercase tracking-[0.15em] text-[zinc-500] hover:text-[#CCAD60] transition-colors font-sans"
                data-testid="finance-hide-link"
              >
                Hide finance options
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Enquiry Form Component
const EnquiryForm = ({ carId, carName }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      const response = await fetch(`${API}/cars/${carId}/enquiries/general`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || null,
          message: formData.message
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to submit enquiry');
      }
      
      setIsSubmitted(true);
      toast.success('Enquiry sent successfully!');
    } catch (err) {
      console.error('Enquiry submission error:', err);
      setError(err.message);
      toast.error(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (isSubmitted) {
    return (
      <div 
        className="bg-[#CCAD60]/10 border border-[#CCAD60]/30 p-6 text-center"
        data-testid="enquiry-success"
      >
        <Check size={32} className="mx-auto text-[#CCAD60] mb-3" />
        <h3 className="font-serif text-xl text-[white] mb-2">Enquiry Sent!</h3>
        <p className="text-[zinc-500] font-sans text-sm">
          Thank you for your interest in this vehicle. We'll be in touch soon.
        </p>
      </div>
    );
  }
  
  return (
    <div data-testid="enquiry-form-container">
      <h3 className="text-[10px] uppercase tracking-[0.2em] text-[#CCAD60] font-sans font-medium mb-4">
        Enquire About This Vehicle
      </h3>
      
      <form onSubmit={handleSubmit} className="space-y-4" data-testid="enquiry-form">
        {/* Name */}
        <div>
          <label className="block text-[10px] uppercase tracking-[0.15em] text-[zinc-500] font-sans mb-2">
            Name <span className="text-red-500">*</span>
          </label>
          <Input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder="Your full name"
            className="bg-[#141414] border-white/10 text-[white] h-12 focus:border-[#CCAD60]"
            data-testid="enquiry-name-input"
          />
        </div>
        
        {/* Email */}
        <div>
          <label className="block text-[10px] uppercase tracking-[0.15em] text-[zinc-500] font-sans mb-2">
            Email <span className="text-red-500">*</span>
          </label>
          <Input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            placeholder="your.email@example.com"
            className="bg-[#141414] border-white/10 text-[white] h-12 focus:border-[#CCAD60]"
            data-testid="enquiry-email-input"
          />
        </div>
        
        {/* Phone (Optional) */}
        <div>
          <label className="block text-[10px] uppercase tracking-[0.15em] text-[zinc-500] font-sans mb-2">
            Phone <span className="text-[zinc-500]">(optional)</span>
          </label>
          <Input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="Your phone number"
            className="bg-[#141414] border-white/10 text-[white] h-12 focus:border-[#CCAD60]"
            data-testid="enquiry-phone-input"
          />
        </div>
        
        {/* Message */}
        <div>
          <label className="block text-[10px] uppercase tracking-[0.15em] text-[zinc-500] font-sans mb-2">
            Message <span className="text-red-500">*</span>
          </label>
          <Textarea
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
            placeholder={`I'm interested in the ${carName}. Please get in touch with more information.`}
            rows={4}
            className="bg-[#141414] border-white/10 text-[white] focus:border-[#CCAD60] resize-none"
            data-testid="enquiry-message-input"
          />
        </div>
        
        {/* Error Message */}
        {error && (
          <div className="bg-red-600/10 border border-red-600/30 p-3 text-red-400 text-sm font-sans" data-testid="enquiry-error">
            {error}
          </div>
        )}
        
        {/* Submit Button */}
        <Button 
          type="submit"
          disabled={isSubmitting}
          className="w-full h-14 bg-[#CCAD60] text-black font-bold uppercase tracking-widest text-sm hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="enquiry-submit-btn"
        >
          {isSubmitting ? (
            <>
              <Loader2 size={18} className="mr-2 animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <Send size={18} className="mr-2" />
              Send Enquiry
            </>
          )}
        </Button>
      </form>
    </div>
  );
};

export default CarDetailPage;
